// Sparkline.tsx — a small, real, pure-SVG line chart. No charting dependency (the codebase has none
// and the bundle is already flagged as large) — a sparkline is simple enough to hand-roll, and it
// renders the SAME numbers already on screen elsewhere (LiftSignal.history), never invented data.
export function Sparkline({
  points, color, width = 96, height = 32,
}: {
  points: [string, number][]; // (date, value), chronological
  color: string;
  width?: number;
  height?: number;
}) {
  if (points.length < 2) return null;
  const values = points.map((p) => p[1]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = 3;
  const xStep = (width - pad * 2) / (points.length - 1);
  const coords = values.map((v, i) => {
    const x = pad + i * xStep;
    const y = pad + (1 - (v - min) / span) * (height - pad * 2);
    return [x, y] as const;
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lastX, lastY] = coords[coords.length - 1];
  const areaPath = `${path} L${lastX.toFixed(1)},${(height - pad).toFixed(1)} L${coords[0][0].toFixed(1)},${(height - pad).toFixed(1)} Z`;
  const gradId = `spark-${color.replace("#", "")}`;
  // Approximate path length (sum of segment lengths) so the draw-in animation's dash offset
  // starts fully hidden and unrolls to the real line — a static line reads as furniture, not data.
  const pathLen = coords.slice(1).reduce((sum, [x, y], i) => {
    const [px, py] = coords[i];
    return sum + Math.hypot(x - px, y - py);
  }, 0);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ flexShrink: 0, overflow: "visible" }} aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
      <path
        className="sparkline-path" d={path} fill="none" stroke={color} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray={pathLen} style={{ "--len": pathLen } as React.CSSProperties}
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
}
